import { ReactElement, useMemo, useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { getLength } from '@scene'
import { toNumber } from 'lodash-es'
import numeral from 'numeral'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { Stack, Typography, Radio, RadioGroup, FormControlLabel, Button } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { UnitField } from '@ui/forms'
import { CollapsibleDivider } from '@ui/structure'

type PreservationMode = 'start' | 'end'

interface Props {
  handleReset?: () => void
}

const WallLengthAdjustmentFields = ({ handleReset }: Props): ReactElement => {
  const { setValue, formState, reset, trigger } = useFormContext()

  // Watch form values for length adjustment and preservation mode
  const lengthAdjustment = useWatch({ name: 'lengthAdjustment' }) as number
  const preservationMode = useWatch({ name: 'preservationMode' }) as PreservationMode

  // Get original wall points from default values
  const originalStart = useMemo(() => {
    if (!formState.defaultValues?.start?.x || !formState.defaultValues?.start?.y) return null
    return new ImmutableVector3(formState.defaultValues.start.x, formState.defaultValues.start.y, 0)
  }, [formState.defaultValues?.start?.x, formState.defaultValues?.start?.y])

  const originalEnd = useMemo(() => {
    if (!formState.defaultValues?.end?.x || !formState.defaultValues?.end?.y) return null
    return new ImmutableVector3(formState.defaultValues.end.x, formState.defaultValues.end.y, 0)
  }, [formState.defaultValues?.end?.x, formState.defaultValues?.end?.y])

  // Function to apply length adjustment based on original geometry
  const handleClickApply = useCallback(() => {
    if (
      !originalStart ||
      !originalEnd ||
      lengthAdjustment === undefined ||
      lengthAdjustment === null
    )
      return

    const dx = originalEnd.x - originalStart.x
    const dy = originalEnd.y - originalStart.y
    const originalLength = Math.sqrt(dx * dx + dy * dy)

    if (originalLength === 0) return // Avoid division by zero

    // Calculate the direction vector (unit vector)
    const directionX = dx / originalLength
    const directionY = dy / originalLength

    // Calculate new length
    const newLength = originalLength + lengthAdjustment

    if (newLength <= 0) return // Don't allow negative or zero length

    let newStartX: number
    let newStartY: number
    let newEndX: number
    let newEndY: number

    if (preservationMode === 'start') {
      // Keep start point, move end point
      newStartX = originalStart.x
      newStartY = originalStart.y
      newEndX = originalStart.x + directionX * newLength
      newEndY = originalStart.y + directionY * newLength
    } else {
      // Keep end point, move start point
      newEndX = originalEnd.x
      newEndY = originalEnd.y
      newStartX = originalEnd.x - directionX * newLength
      newStartY = originalEnd.y - directionY * newLength
    }

    // Update the form values directly
    setValue('start.x', toNumber(newStartX))
    setValue('start.y', toNumber(newStartY))
    setValue('end.x', toNumber(newEndX))
    setValue('end.y', toNumber(newEndY))

    // Trigger form validation to ensure the subscription is called
    trigger(['start.x', 'start.y', 'end.x', 'end.y'])
  }, [lengthAdjustment, preservationMode, setValue, trigger, originalStart, originalEnd])

  // Function to reset to original geometry
  const handleClickReset = useCallback(() => {
    reset()
    setValue('lengthAdjustment', 0)
    setValue('preservationMode', 'start')
    if (
      !originalStart ||
      !originalEnd ||
      lengthAdjustment === undefined ||
      lengthAdjustment === null
    )
      return
    setValue('start.x', toNumber(originalStart.x))
    setValue('start.y', toNumber(originalStart.y))
    setValue('end.x', toNumber(originalEnd.x))
    setValue('end.y', toNumber(originalEnd.y))
    handleReset?.()
  }, [reset, setValue, originalStart, originalEnd, lengthAdjustment, handleReset])

  const originalLength = useMemo(() => {
    if (!originalStart || !originalEnd) return 0
    return getLength(originalStart, originalEnd)
  }, [originalStart, originalEnd])

  const newLength = useMemo(() => {
    return originalLength + (lengthAdjustment || 0)
  }, [originalLength, lengthAdjustment])

  // Handle direct final length input
  const handleFinalLengthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isEmpty = event.target.value.length === 0
      if (isEmpty) {
        setValue('lengthAdjustment', 0)
        return
      }
      const finalLength = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
      const adjustment = finalLength - originalLength
      setValue('lengthAdjustment', adjustment)
    },
    [originalLength, setValue],
  )

  return (
    <CollapsibleDivider text="Länge ändern" defaultExpanded={false}>
      <Stack direction="column" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <Typography>um</Typography>
            <UnitField
              name="lengthAdjustment"
              label=""
              internalUnit="m"
              displayUnit="m"
              placeholder="0.00"
              numeralFormatter="0.00"
              size="small"
              sx={{ width: '100px' }}
            />
            <Typography>verlängern</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <RadioGroup
              value={preservationMode}
              onChange={event => {
                setValue('preservationMode', event.target.value as PreservationMode)
              }}
              aria-label="preservation mode"
              row
              sx={{ width: '100%', padding: 0, margin: 0 }}
            >
              <FormControlLabel
                value="start"
                control={<Radio size="small" sx={{ padding: 0 }} />}
                label={'Start'}
                labelPlacement="bottom"
                sx={{ color: 'red' }}
              />
              <FormControlLabel
                value="end"
                control={<Radio size="small" sx={{ padding: 0 }} />}
                label={'Ende'}
                labelPlacement="bottom"
              />
            </RadioGroup>
            <Typography>fixieren</Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <Stack direction="row" spacing={1} alignItems={'center'}>
              {originalLength > 0 && (
                <>
                  <Typography noWrap>{numeral(originalLength).format('0.00')} m</Typography>
                  <ArrowRightAltIcon />
                </>
              )}
            </Stack>
            <UnitField
              name="finalLength"
              label=""
              internalUnit="m"
              displayUnit="m"
              placeholder="0.00"
              numeralFormatter="0.00"
              size="small"
              value={newLength}
              onChange={handleFinalLengthChange}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClickApply}
              disabled={lengthAdjustment === undefined || lengthAdjustment === null}
              fullWidth
            >
              Anwenden
            </Button>
            <Button size="small" onClick={handleClickReset} fullWidth variant="outlined">
              Zurücksetzen
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </CollapsibleDivider>
  )
}

export default WallLengthAdjustmentFields
